import { FunctionComponent, useContext, useEffect, useRef } from 'react';
import { NewsletterSignup, Environment, CommunityApp } from '@atlassian/learning-components';
import { css } from '@compiled/react';
import { UserContext } from 'context/UserContext';
import { getAnalyticsAnonymousId } from 'utils/utilities';
import { sendTrackEvent, sendUIEvent } from 'services/analytics';
import LOGGER from 'services/sentry/sentry';

export const getApiEnv = () => {
  switch (process.env.REACT_APP_ENVI) {
    case 'production':
      return Environment.PRODUCTION;
    case 'staging':
      return Environment.STAGING;
    default:
      return Environment.DEVELOPMENT;
  }
};

const onNewsletterClick = async (app: CommunityApp, atlassianId?: string) => {
  await sendUIEvent(
    `${app}Newsletter`, // eg. 'homeNewsletter' - indicates which area of the app it's sent from
    'button',
    'clicked',
    'communityNewsletterSignup',
    {},
    atlassianId,
  );
};

const onPrivacyPolicyClick = async (app: CommunityApp, atlassianId?: string) => {
  await sendUIEvent(`${app}Newsletter`, 'link', 'clicked', 'communityNewsletterPrivacyPolicy', {}, atlassianId);
};

const onNewsletterSignupSuccess = async (app: CommunityApp, atlassianId?: string) => {
  await sendTrackEvent(`${app}Newsletter`, 'userCommunityNewsletterSignup', 'succeeded', {}, atlassianId);
};

const onNewsletterSignupError = async (app: CommunityApp, atlassianId?: string) => {
  await sendTrackEvent(`${app}Newsletter`, 'userCommunityNewsletterSignup', 'failed', {}, atlassianId);
};

type NewsletterProps = {
  app: CommunityApp;
};

const Newsletter: FunctionComponent<NewsletterProps> = ({ app }) => {
  const subscribeRef = useRef<HTMLDivElement | null>(null);

  const data = useContext(UserContext);
  const user = data?.user;

  const anonymousId = getAnalyticsAnonymousId();
  const atlassianId = user?.account_id;

  const pageUrl = window.location.href;

  useEffect(() => {
    if (window.location.hash === '#subscribe' && subscribeRef.current) {
      subscribeRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return (
    <div id="subscribe" css={containerStyle} ref={subscribeRef}>
      <NewsletterSignup
        anonymousId={anonymousId}
        atlassianId={atlassianId}
        environment={getApiEnv()}
        formUrl={pageUrl}
        eventHandlers={{
          onSubmit: async () => await onNewsletterClick(app, atlassianId),
          onSuccess: async () => await onNewsletterSignupSuccess(app, atlassianId),
          onPrivacyPolicyClick: async () => await onPrivacyPolicyClick(app, atlassianId),
          onError: async (error) => {
            LOGGER.error(error);
            await onNewsletterSignupError(app, atlassianId);
          },
        }}
      />
    </div>
  );
};

export default Newsletter;

const containerStyle = css({
  width: '100%',
  padding: '0 1rem',
});
